import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ColorPreset, LightDevice } from '../../../interfaces/lights.interface';
import { CustomLightColorName, OnOff, RoomName } from 'src/graphql/generated/schema';
import { combineLatest, Observable, switchMap, take, takeUntil, startWith } from 'rxjs';
import { ModalController, RangeCustomEvent, ToastController } from '@ionic/angular';
import { CommonPage } from 'src/app/shared/common-page/common-page';
import { getCodeFromColor, getColorFromCode } from 'src/app/shared/helpers/color-convert-helper';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';
import { IShadowState } from 'src/mqtt/interfaces/interfaces';
import { actionId, actionType, roomsNames } from 'src/mqtt/actions-list';
import { LightsControlService } from 'src/app/shared/services/lights-control.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { BrightnessBoundsService } from 'src/app/shared/services/brightness-bounds.service';
import { brightness255ToPercent } from 'src/app/shared/helpers/brightness-helper';


@Component({
  selector: 'light-settings',
  templateUrl: './light-settings.component.html',
  styleUrls: ['./light-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LightSettingsComponent extends CommonPage implements OnInit, OnDestroy {
  public static WHITE_LIGHT: string = '#FFFFFF';
  public static DEFAULT_COLOR: string = '#13ABD7'

  @Input() lightDevice!: LightDevice;
  @Input() colorPresets: ColorPreset[] = [];
  // Per-device memory of the preset the resident explicitly tapped, keyed by deviceId.
  // Owned by the parent so it survives this component being destroyed on close/reopen.
  @Input() selectedPresetByDevice: Map<string, number> = new Map();
  @Output() onClose: EventEmitter<void> = new EventEmitter();

  public isBrightnessInvalid: boolean = true;
  public lightBrightness?: number = 0;
  public minBrightness?: number = 0;
  public maxBrightness?: number = 100;
  public isLightOn: boolean = false;
  private userIsDraggingBrightness = false;
  private dragEchoTimer?: ReturnType<typeof setTimeout>;
  // The last brightness the light actually reported, kept even while a drag guard is
  // suppressing display updates, so the guard's expiry has a real value to adopt.
  private lastReportedPct?: number;

  public lightsCircadian?: string
  public selectedLightColor: string = LightSettingsComponent.WHITE_LIGHT;
  // Once the resident picks any color (including white), stop auto-adopting the device
  // color from the shadow. Otherwise enabling white re-arms the adopt (selectedLightColor
  // is white again) and a later echo overwrites it with DEFAULT_COLOR, snapping the picker
  // knob to the leftmost color.
  private userHasSetColor = false;

  public savePresetBtnTitle: string = '';
  public isSavePresetBtnVisible: boolean = false;
  private selectedPresetIndex: number | undefined;
  // Bound to the presets radio-group value. Cleared to '' when a slider color is picked
  // so the previously-selected preset can be re-tapped (an ion-radio-group won't re-emit
  // ionChange for its already-selected value).
  public selectedPresetValue: number | '' = '';
  // The circadian toast currently on screen — only one is shown at a time. Held as the
  // create() promise so a rapid follow-up selection dismisses it even before it resolves.
  private activeCircadianToast?: Promise<HTMLIonToastElement>;

  constructor(
    private ref: ChangeDetectorRef,
    public toastController: ToastController,
    public modalCtrl: ModalController,
    private mqtt: MqttConnectorService,
    private lightsControlService: LightsControlService,
    private analyticsService: AnalyticsService,
    private bounds: BrightnessBoundsService,
  ) {
    super(toastController, modalCtrl);
  }

  public ngOnInit() {
    this.bounds.floor$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.minBrightness = value;
      // Park at the floor only while the light is off AND the resident is not
      // driving the slider. These bounds re-emit on EVERY shadow message (their
      // source is items$ -- the whole message map -- with no distinctUntilChanged),
      // so without the drag guard the helper write that a drag itself causes snaps
      // the thumb straight back to the floor.
      if (!this.isLightOn && !this.userIsDraggingBrightness) {
        this.lightBrightness = value;
      }
      this.ref.markForCheck();
    });

    this.bounds.ceiling$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.maxBrightness = value;
      this.ref.markForCheck();
    });

    this.mqtt.getMessageListener(
      `${actionType.light}.${this.lightDevice.deviceId}${actionId.lights}`
    ).pipe(takeUntil(this.destroy$)).subscribe((message: IShadowState | undefined) => {
      if (!message?.reported) return;
      const wasOn = this.isLightOn;
      this.isLightOn = message.reported.state === 'on';
      const pct = brightness255ToPercent(message.reported.attributes?.['brightness']);
      if (pct !== undefined) this.lastReportedPct = pct;
      // A light coming on IS the confirmation that a drag-from-off landed. Adopt the
      // real level and release the drag guard now, instead of holding a stale thumb
      // for the rest of the timeout and then never being re-triggered.
      if (!wasOn && this.isLightOn && pct !== undefined) {
        this.userIsDraggingBrightness = false;
        clearTimeout(this.dragEchoTimer);
      }
      // While the resident is dragging, echoes must not fight the thumb.
      if (!this.userIsDraggingBrightness) {
        if (!this.isLightOn) {
          // Parked at the floor while off: that is where a drag starts, not a
          // prediction of what the light will restore to.
          this.lightBrightness = this.minBrightness;
        } else if (pct !== undefined) {
          this.lightBrightness = pct;
        }
        // Otherwise the light is ON but reports no brightness — a group whose
        // member has gone 'unknown' (seen on the AL dev unit, where
        // light.livingspace_a_light_b was unknown), or one mid-transition. Keep
        // the last known level: "I cannot read it right now" is not "it is at the
        // floor", and treating it as the floor made the slider snap to 10% while
        // the room was visibly brighter.
      }
      // Bounds always resolve now (service value or fallback), so the only
      // remaining unknown was the light's state — which we just read.
      this.isBrightnessInvalid = false;
      this.ref.markForCheck();
    });

    this.mqtt.getMessageListener(
      `${actionType.inputText}.${this.lightDevice.deviceId}${actionId.lightColor}`
    ).pipe(takeUntil(this.destroy$)).subscribe((message: IShadowState | undefined) => {
      if (message?.reported?.entity_id && message?.reported?.attributes) {
        // Only adopt the device color for the INITIAL restore — never after the resident
        // has picked a color, or enabling white would let an echo snap the knob leftward.
        if (!this.userHasSetColor && this.selectedLightColor === LightSettingsComponent.WHITE_LIGHT) {
          this.selectedLightColor = getColorFromCode(message?.reported?.state) || LightSettingsComponent.DEFAULT_COLOR;
          setTimeout(() => this.ref.markForCheck(), 0);
        }
      }
    });

    // Subscribe to the MASTER circadian state (livingspace_a) that controls ALL lights
    const circadianEntityId = `${actionType.inputBoolean}.livingspace_a${actionId.circadianLight}`;
    console.log('[LightSettings] Subscribing to circadian entity:', circadianEntityId);
    
    this.mqtt.getMessageListener(circadianEntityId).pipe(
      takeUntil(this.destroy$),
      // Add startWith to get initial value from cache if available
      startWith(undefined)
    ).subscribe((message: IShadowState | undefined) => {
      if (message?.reported?.entity_id) {
        const newCircadianState = this.defineOnOff([<OnOff>message?.reported?.state]);
        
        // Log state changes for debugging
        if (this.lightsCircadian !== newCircadianState) {
          console.log(`[LightSettings] Master circadian state changed: ${this.lightsCircadian} -> ${newCircadianState}`);
        }
        
        this.lightsCircadian = newCircadianState;
        // Use markForCheck to schedule change detection
        this.ref.markForCheck();
      }
    });

    // Reopening rebuilds this component (and the parent clears preset.selected), so
    // re-highlight whichever preset the resident explicitly tapped for this light.
    this.restoreSelectedPreset();
  }

  // Restore the highlight from the preset the resident explicitly tapped for this light
  // (remembered per-device in selectedPresetByDevice, which outlives this component). We
  // deliberately do NOT derive the highlight from the current color: a slider color can
  // equal a preset's color, and that must not light up the preset.
  private restoreSelectedPreset(): void {
    const remembered = this.selectedPresetByDevice.get(this.lightDevice.deviceId);
    this.selectedPresetValue = remembered ?? '';
    this.selectedPresetIndex = remembered;
    this.colorPresets?.forEach((preset) => (preset.selected = preset.id === remembered));
  }

  public get brightnessLabel(): string {
    return this.isLightOn ? `${this.lightBrightness}%` : 'Off';
  }

  /**
   * Armed on knob-move-START, not on release.
   *
   * The range is bound with [(ngModel)]="lightBrightness", so ANY programmatic write
   * to that field physically moves the knob. Arming the guard only in setBrightness
   * (knob-move-END) left the whole gesture unguarded: an echo landing mid-drag
   * overwrote lightBrightness, ngModel yanked the knob to that value under the
   * resident's finger, and the release then wrote the yanked position. Observed on
   * the AL dev unit — aimed for 75, the helper recorded 36.
   */
  public onBrightnessDragStart(): void {
    this.userIsDraggingBrightness = true;
    clearTimeout(this.dragEchoTimer);
  }

  public setBrightness(event: RangeCustomEvent) {
    const value: number = <number>event.detail.value;

    this.userIsDraggingBrightness = true;
    clearTimeout(this.dragEchoTimer);
    this.dragEchoTimer = setTimeout(() => {
      this.userIsDraggingBrightness = false;
      // Adopt what the light actually settled at. The echo carrying the real value
      // arrives while the guard is up and is ignored, and nothing re-triggers after
      // it expires -- so without this the thumb keeps showing the REQUESTED level.
      // It differs in practice: the 60% limiter clamps some rooms on this fleet.
      if (this.isLightOn && this.lastReportedPct !== undefined) {
        this.lightBrightness = this.lastReportedPct;
      }
      this.ref.markForCheck();
    }, 1500);
    this.lightBrightness = value;

    this.analyticsService.trackWidgetInteraction('lights', 'brightness_change', { deviceId: this.lightDevice.deviceId, value });

    this.lightsControlService.setBrightness(this.lightDevice.deviceId, value).subscribe(
      () => this.ref.markForCheck()
    );
  }

  public setColor(value: any, fromPreset: boolean = false) {
    const wasWhite = this.selectedLightColor?.toUpperCase() === LightSettingsComponent.WHITE_LIGHT;
    // The resident is now driving the color; the shadow must not adopt over their choice.
    this.userHasSetColor = true;
    this.selectedLightColor = value;

    if (!fromPreset) {
      // Slider selection: drop the preset highlight so the resident can re-tap the preset
      // they came from (an ion-radio-group won't re-emit ionChange for its current value).
      // Also forget any remembered preset so this custom color doesn't re-highlight a
      // preset on reopen, even if it happens to equal that preset's color.
      this.selectedPresetValue = '';
      this.colorPresets.forEach((preset) => (preset.selected = false));
      this.isSavePresetBtnVisible = false;
      this.selectedPresetByDevice.delete(this.lightDevice.deviceId);
    }

    // Toast only while Circadian Cycle is ON in Settings, and only on a state change:
    // color -> white => enabled; white -> color => overridden. A same-state selection
    // (white -> white, already enabled; or color -> color, already overridden) leaves
    // circadian unchanged, so no toast.
    const isWhite = value?.toUpperCase() === LightSettingsComponent.WHITE_LIGHT;
    let circadianToast: string | undefined;
    if (isWhite && !wasWhite) {
      circadianToast = 'Circadian Cycle Enabled';
    } else if (!isWhite && wasWhite) {
      circadianToast = 'Circadian Cycle Overridden';
    }
    if (circadianToast && this.lightsCircadian === 'ON') {
      this.presentCircadianToast(circadianToast);
    }

    this.analyticsService.trackWidgetInteraction('lights', 'color_change', { deviceId: this.lightDevice.deviceId, color: value });

    // Only chain a turn-on when the light is actually OFF. That chain exists so
    // picking a colour on a dark room lights it; when the room is already on,
    // `Lights - Set Color` has already applied the colour with its own 2s fade, and
    // the chained turn-on re-applies rgbw + brightness with a 1s transition a
    // fraction of a second later -- visibly cutting that fade short. Measured on the
    // AL dev unit: Set Color at .383, script.lights_turn_on at .727, 344ms apart.
    const applyColor = this.lightsControlService.setLightColor(
      this.lightDevice.deviceId,
      getCodeFromColor(value),
    );
    const request = this.isLightOn
      ? applyColor
      : applyColor.pipe(
          switchMap(() => this.lightsControlService.switchLight(this.lightDevice.deviceId, true)),
        );

    request.subscribe(() => this.ref.markForCheck());
  }

  // Keep only one circadian toast on screen. Claim the slot synchronously (with the
  // create() promise) and dismiss the previous one, so two rapid selections can't leave
  // two toasts up even if the first create() hasn't resolved yet.
  private presentCircadianToast(message: string) {
    const previous = this.activeCircadianToast;
    const toastPromise = this.toastController.create({
      message,
      duration: 1000,
      position: 'bottom',
      cssClass: 'circadian-toast',
    });
    this.activeCircadianToast = toastPromise;

    previous?.then((toast) => toast.dismiss());
    toastPromise.then((toast) => {
      toast.onDidDismiss().then(() => {
        if (this.activeCircadianToast === toastPromise) {
          this.activeCircadianToast = undefined;
        }
      });
      toast.present();
    });
  }

  public ngOnDestroy(): void {
    // Don't leave a circadian toast on screen after the light panel closes.
    this.activeCircadianToast?.then((toast) => toast.dismiss());
    clearTimeout(this.dragEchoTimer);
    super.ngOnDestroy();
  }

  // Driven by a direct tap on the swatch, not ion-radio-group's ionChange — that fired
  // unreliably per platform/position, so some presets never applied their color (VIBU-37).
  public onPresetTap(preset: ColorPreset) {
    this.selectedPresetIndex = preset.id;
    this.selectedPresetValue = preset.id;
    this.isSavePresetBtnVisible = false;
    this.colorPresets.forEach((p) => (p.selected = p.id === preset.id));

    this.analyticsService.trackWidgetInteraction('lights', 'preset_selected', { presetId: preset.id });

    if (preset.value) {
      // Remember this explicit preset tap so its highlight is restored on reopen. Set it
      // AFTER setColor, since setColor (fromPreset=true) doesn't clear the memory but a
      // slider move would — this is the deliberate "you picked a preset" signal.
      this.setColor(preset.value, true);
      this.selectedPresetByDevice.set(this.lightDevice.deviceId, preset.id);
    } else {
      this.isSavePresetBtnVisible = true;
      this.savePresetBtnTitle = `Save as preset ${preset.id + 1}`;
    }
    this.ref.markForCheck();
  }

  public savePreset() {
    this.isSavePresetBtnVisible = false;
    if (typeof this.selectedPresetIndex === 'number') {
      this.updatePreset(this.selectedPresetIndex, this.selectedLightColor);
    }
  }

  public deletePreset(presetId: number) {
    this.colorPresets[presetId].value = undefined;
    this.isSavePresetBtnVisible = true;
    this.savePresetBtnTitle = `Save as preset ${presetId + 1}`;
    this.updatePreset(presetId);
  }

  public getColor(presetId: number): string {
    const result = this.colorPresets.find((preset) => preset.id === presetId)?.value;
    return `--preset-color: ${ result || 'transparent' }`
  }

  public updatePreset(presetId: number, color?: string) {
    const preset = this.colorPresets[presetId];
    const newColor = color || LightSettingsComponent.WHITE_LIGHT;
    const colorCode = getCodeFromColor(newColor);
    const entityId = `input_text.${preset.entityName}`;

    this.mqtt.setInputTextValue(entityId, colorCode).pipe(
      take(1)
    ).subscribe({
      next: () => {
        this.colorPresets[preset.id].value = color;
        if (color) {
          this.analyticsService.trackWidgetInteraction('lights', 'custom_color_saved', { presetId: preset.id });
        }
        this.ref.markForCheck();
      },
      error: (err) => {
        console.error('Error updating light color:', err);
        this.showError('Error updating light color');
      }
    });
  }

  private showError(message: string) {
    this.toastController.create({
      message,
      duration: 2000,
      position: 'bottom',
      color: 'danger'
    }).then(toast => toast.present());
  }
}
